import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/app/App";
import { Providers } from "@/app/providers";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { initOverlayScrollbar } from "@/shared/lib/overlay-scrollbar";
/* Last, and deliberately so: sonner injects its stylesheet when the module is
   evaluated, and importing it above `@/index.css` reorders every `<style>` tag
   in the document (which the visual baseline catches, correctly). App already
   pulls it in for `<Toaster/>`, so this import changes no order at all. */
import { toast } from "sonner";

initOverlayScrollbar();

/* Everything React's own error handling cannot see: a throw in a non-React
   callback, a rejected promise nobody awaited. Both used to end the same way —
   a line in a console nobody has open (00-PLAN.md, the no-silent rule).
   Deduped by message, because a broken interval fires this on every tick. */
function reportGlobal(source: string, err: unknown): void {
  console.error(source, err);
  const message = err instanceof Error ? err.message : String(err ?? source);
  toast.error(message, { id: `global:${message}` });
}

window.addEventListener("error", (e) => reportGlobal("window.error", e.error ?? e.message));
window.addEventListener("unhandledrejection", (e) => reportGlobal("unhandledrejection", e.reason));

// MSW is opt-in and off by default: unset REACT_APP_API_MOCK returns before the
// dynamic import, so the worker chunk is never fetched and no service worker is
// registered. Flipping it needs no code change once the backend lands.
async function enableMocking() {
  if (process.env.REACT_APP_API_MOCK !== "true") return;
  const { worker } = await import("@/mocks/browser");
  return worker.start({ onUnhandledRequest: "bypass" });
}

// public/index.html always ships the mount node.
const root = ReactDOM.createRoot(document.getElementById("root")!);
enableMocking().then(() => {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <Providers>
          <App />
        </Providers>
      </ErrorBoundary>
    </React.StrictMode>,
  );
});
